import { defineAsyncComponent, type AsyncComponentLoader } from 'vue'
import { Spin } from 'ant-design-vue'

/**
 * 创建异步组件，支持加载状态和错误处理
 * @param loader 组件加载函数
 * @param options 配置选项
 * @returns 异步组件
 */
export function createAsyncComponent(
  loader: AsyncComponentLoader,
  options: {
    loadingComponent?: any
    errorComponent?: any
    delay?: number
    timeout?: number
    retryCount?: number
  } = {}
) {
  const {
    loadingComponent = Spin,
    errorComponent = null,
    delay = 200,
    timeout = 3000,
    retryCount = 3
  } = options

  return defineAsyncComponent({
    loader,
    loadingComponent,
    errorComponent,
    delay,
    timeout,
    onError(error, retry, fail, attempts) {
      if (attempts <= retryCount) {
        // 重试加载
        retry()
      } else {
        // 加载失败
        console.error('Component load failed:', error)
        fail()
      }
    }
  })
}

/**
 * 预加载组件（用于预加载关键组件）
 * @param loader 组件加载函数
 */
export function preloadComponent(loader: AsyncComponentLoader) {
  // 在空闲时间预加载组件
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      loader()
    })
  } else {
    // 降级到setTimeout
    setTimeout(() => {
      loader()
    }, 1000)
  }
}

/**
 * 路由级别的组件懒加载
 * @param importFn 动态导入函数
 * @param fallback 加载中的组件
 * @returns 异步组件
 */
export function lazyRouteComponent(
  importFn: () => Promise<any>,
  fallback?: any
) {
  return createAsyncComponent(importFn, {
    loadingComponent: fallback || Spin,
    delay: 100,
    timeout: 5000
  })
}

/**
 * 管理模块的懒加载组件
 */
export const AdminComponents = {
  UserManagement: () => import('@/modules/user/views/UserManagement.vue'),
  OrganizationManagement: () => import('@/modules/system/views/OrganizationManagement.vue'),
  LanguageManagement: () => import('@/modules/system/views/LanguageManagement.vue'),
  SystemConfigManagement: () => import('@/modules/system/views/SystemConfigManagement.vue'),
  APIConfigManagement: () => import('@/modules/system/views/APIConfigManagement.vue'),
  ClientConfigManagement: () => import('@/modules/system/views/ClientConfigManagement.vue'),
  UsageManagement: () => import('@/modules/usage/views/UsageManagement.vue'),
  HotWordManagement: () => import('@/modules/system/views/HotWordManagement.vue')
}

/**
 * 认证模块的懒加载组件
 */
export const AuthComponents = {
  LoginView: () => import('@/modules/auth/views/LoginView.vue'),
  RegisterView: () => import('@/modules/auth/views/RegisterView.vue')
}

/**
 * 布局模块的懒加载组件
 */
export const LayoutComponents = {
  MainLayout: () => import('@/components/layout/MainLayout.vue'),
  SideNavigation: () => import('@/components/layout/SideNavigation.vue'),
  BreadcrumbNavigation: () => import('@/components/common/BreadcrumbNavigation.vue'),
  LanguageSelector: () => import('@/components/common/LanguageSelector.vue')
}

/**
 * 仪表板模块的懒加载组件
 */
export const DashboardComponents = {
  DashboardView: () => import('@/modules/dashboard/views/DashboardView.vue'),
  UsageOverview: () => import('@/modules/dashboard/views/UsageOverview.vue')
}

/**
 * 用量模块的懒加载组件
 */
export const UsageComponents = {
  UsageStatisticsChart: () => import('@/modules/usage/components/UsageStatisticsChart.vue')
}
